import React from 'react';
import { colors, typography, spacing } from '../../styles/design-system';

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize?: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  loading?: boolean;
  showPageInfo?: boolean;
  style?: React.CSSProperties;
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  loading = false,
  showPageInfo = true,
  style
}) => {
  const {
    currentPage,
    totalPages,
    totalItems,
    hasNext = currentPage < totalPages,
    hasPrev = currentPage > 1
  } = pagination;

  const handlePrev = () => {
    if (hasPrev && !loading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (hasNext && !loading) {
      onPageChange(currentPage + 1);
    }
  };

  const displayTotalPages = Math.max(1, totalPages);

  return (
    <div style={{ ...styles.container, ...style }}>
      <button
        style={{
          ...styles.button,
          ...(hasPrev && !loading ? {} : styles.buttonDisabled)
        }}
        onClick={handlePrev}
        disabled={!hasPrev || loading}
      >
        이전
      </button>

      {showPageInfo && (
        <div style={styles.info}>
          {currentPage} / {displayTotalPages} 페이지 (전체 {totalItems}개)
        </div>
      )}

      <button
        style={{
          ...styles.button,
          ...(hasNext && !loading ? {} : styles.buttonDisabled)
        }}
        onClick={handleNext}
        disabled={!hasNext || loading}
      >
        다음
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing[7],
    padding: `${spacing[5]} 0`,
  },
  button: {
    padding: `${spacing[2]} ${spacing[5]}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    color: colors.gray[800],
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '70px',
    whiteSpace: 'nowrap' as const,
    outline: 'none',
  },
  buttonDisabled: {
    backgroundColor: colors.gray[100],
    color: colors.gray[400],
    cursor: 'not-allowed',
  },
  info: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
    fontWeight: typography.fontWeight.medium,
    whiteSpace: 'nowrap' as const,
  },
};

export default Pagination;
